import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from "./ui/menubar";
import { ArrowLeft, Heart, Menu, ShoppingCart, Trash2, User } from "lucide-react";
import { useSupabaseContext } from "../lib/supabase_context";
import { Link, useLocation, useNavigate } from "@remix-run/react";
import type { Session } from "@supabase/supabase-js";
import { useCart } from "../context/cartContext";
import {
  CartPricingFooter,
  CartUnitPriceLabel,
  CartLineTotalRight,
} from "./CartMoneyDisplay";
import Button from "./theme/Button";
import { useFavorites } from "../context/favoriteContext";
import { useRef, useState } from "react";
import { useToast } from "../hooks/use-toast";
import { slugify } from "../lib/helpers/storehelpers";
import GlobalSearch from "./GlobalSearch";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { buildLoginPathWithRedirect } from "../lib/redirect";

interface IProps {
  session?: Session;
  userName?: string;
  userId?: string;
  isAdmin?: boolean;
  category?: string;
  onSelectedCategoryChange?: (value: string) => void;
  hideCart?: boolean;
}

export default function Header(props: IProps) {
  const supabaseContext = useSupabaseContext();
  const { cartItems, updateQuantity, removeFromCart } = useCart();
  const { favorites, openFavoriteLoginPrompt } = useFavorites();
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const loginPath = buildLoginPathWithRedirect(
    `${location.pathname}${location.search}`,
  );

  const increaseQuantity = async (id: string) => {
    try {
      const item = cartItems?.find((item) => item.id === id);
      if (item) {
        updateQuantity(id, item.quantity + 1);
      }
    } catch (error) {
      toast({
        title: "Failed to increase quantity.",
        description: `Please try again`,
        variant: "destructive",
      });
    }
  };

  const decreaseQuantity = async (id: string) => {
    try {
      const item = cartItems?.find((item) => item.id === id);
      if (item) {
        updateQuantity(id, item.quantity - 1);
      }
    } catch (error) {
      toast({
        title: "Failed to decrease quantity.",
        description: `Please try again`,
        variant: "destructive",
      });
    }
  };

  const deleteFromCart = async (id: string) => {
    removeFromCart(id);
  };

  const handleLogout = async () => {
    await supabaseContext.supabase.auth.signOut();
    setIsMobileMenuOpen(false);
    navigate("/");
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="isolate z-50 bg-white shadow-md fixed top-0 left-0 right-0">
      {/* Desktop: grid + overflow-x-clip on search column so the input stack can never
          paint or hit-test over the brand (Chrome flex overflow / compositing edge cases). */}
      <div className="hidden grid-cols-[minmax(0,auto)_minmax(0,1fr)_auto] items-center gap-x-8 bg-white px-4 py-2 lg:grid">
        <Link
          to="/"
          aria-label="BulkBuyPlace home"
          className="relative z-20 flex h-12 min-w-0 max-w-full cursor-pointer items-center gap-2 self-stretch rounded-md px-2 no-underline text-inherit hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring outline-offset-2"
        >
          <img
            src="/images/cardboard_logo.svg"
            className="h-10 w-10 shrink-0 select-none"
            alt=""
            draggable={false}
          />
          <span className="min-w-0 truncate font-bold text-lg tracking-wide select-none">
            BulkBuyPlace
          </span>
        </Link>

        <div className="relative z-0 flex min-h-0 min-w-0 max-w-full items-center justify-self-stretch self-stretch overflow-x-clip overflow-y-visible">
          <GlobalSearch category={props.category} />
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 self-stretch">
          {/* Sign In Button */}
          {props.userId && (
            <Menubar className="border-none">
              <MenubarMenu>
                <MenubarTrigger className="text-sm px-2 py-1">
                  <span className="hidden xl:inline">{`Hi ${props.userName}`}</span>
                  <span className="xl:hidden">{props.userName?.split(" ")[0]}</span>
                  <User className="ml-2 h-4 w-4" />
                </MenubarTrigger>
                <MenubarContent>
                  <MenubarItem onClick={() => navigate("/user")}>
                    Account
                  </MenubarItem>
                  <MenubarSeparator />
                  <MenubarItem onClick={() => navigate("/user/orders")}>
                    Orders
                  </MenubarItem>
                  <MenubarSeparator />
                  <MenubarItem onClick={handleLogout}>Logout</MenubarItem>
                </MenubarContent>
              </MenubarMenu>
            </Menubar>
          )}

          {props.userId && props.isAdmin && (
            <Button mode="secondary" size="sm" className="gap-2">
              <Link to="/admin" className="no-underline flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Return to admin
              </Link>
            </Button>
          )}

          {!props.userId && (
            <Button mode="secondary" size="sm">
              <Link to={loginPath} className="no-underline">
                Sign In
              </Link>
            </Button>
          )}

          <div className="mx-1 h-6 w-px bg-gray-200" />

          {/* Shopping Cart */}
          {!props.hideCart && (
            <div
              className="relative"
              onMouseEnter={() => {
                if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
                setIsHovered(true);
              }}
              onMouseLeave={() => {
                hoverTimeout.current = setTimeout(
                  () => setIsHovered(false),
                  100,
                );
              }}
            >
              <Button
                mode="ghost"
                size="sm"
                onClick={() => navigate("/cart")}
                className="relative flex items-center gap-1"
                aria-label="Cart"
              >
                <ShoppingCart className="h-4 w-4" />
                {cartItems?.length > 0 && (
                  <span className="ml-1 text-xs">
                    (
                    {cartItems.reduce(
                      (total, item) => total + item.quantity,
                      0,
                    )}
                    )
                  </span>
                )}
              </Button>

              {
                /* <Button mode="ghost" size="sm" onClick={() => navigate("/cart")}>
              <ShoppingCart className="h-4 w-4" style={{ color: "#EC5011" }} />
              {cartItems?.length > 0 && (
                <span className="ml-0 text-xs">
                  ({cartItems.reduce((total, item) => total + item.quantity, 0)}
                  )
                </span>
              )}
            </Button> */
              }

              {/* Cart Dropdown */}
              {isHovered && cartItems?.length > 0 && (
                <div
                  className="absolute right-0 top-full mt-2 w-[400px] bg-white shadow-lg p-4 rounded-md border"
                  onMouseEnter={() => {
                    if (hoverTimeout.current) {
                      clearTimeout(hoverTimeout.current);
                    }
                    setIsHovered(true);
                  }}
                  onMouseLeave={() => {
                    hoverTimeout.current = setTimeout(
                      () => setIsHovered(false),
                      100,
                    );
                  }}
                >
                  <h3 className="font-semibold text-sm mb-3">Shopping Cart</h3>
                  <div className="max-h-[250px] overflow-y-auto scrollbar-hide">
                    {cartItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center space-x-3 mb-3 pb-3 border-b last:border-b-0"
                      >
                        <img
                          src={item.image || "/images/placeholder.svg"}
                          alt={item.name}
                          className="w-12 h-12 object-cover rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {item.name}
                          </div>
                          {item.store_name && (
                            <div className="text-xs text-blue-600 hover:text-blue-800">
                              <Link
                                to={`/store/${slugify(item.store_name)}/home`}
                              >
                                {item.store_name}
                              </Link>
                            </div>
                          )}
                          <CartUnitPriceLabel item={item} />
                        </div>
                        <div className="flex flex-col items-end space-y-1">
                          <div className="flex items-center space-x-1">
                            <Button
                              mode="ghost"
                              size="sm"
                              onClick={() => decreaseQuantity(String(item.id))}
                              disabled={item.quantity <= 1}
                              className="h-6 w-6 p-0"
                            >
                              -
                            </Button>
                            <span className="text-sm w-6 text-center">
                              {item.quantity}
                            </span>
                            <Button
                              mode="ghost"
                              size="sm"
                              onClick={() => increaseQuantity(String(item.id))}
                              className="h-6 w-6 p-0"
                            >
                              +
                            </Button>
                          </div>
                          <Button
                            mode="ghost"
                            size="sm"
                            onClick={() => deleteFromCart(String(item.id))}
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <CartPricingFooter
                    items={cartItems}
                    className="mt-3 pt-3 border-t"
                  />
                  <Button
                    mode="primary"
                    onClick={() => navigate("/cart")}
                    className="w-full mt-3"
                    size="sm"
                  >
                    View Cart (
                    {cartItems.reduce(
                      (total, item) => total + item.quantity,
                      0,
                    )}
                    )
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Favorite Items Icon */}
          <Button
            mode="ghost"
            size="sm"
            className="relative"
            aria-label="Favorites"
            onClick={() => {
              if (props.userId) {
                navigate("/user/favorites");
              } else {
                openFavoriteLoginPrompt();
              }
            }}
          >
            <span className="flex items-center space-x-1">
              <Heart
                className={`h-4 w-4 ${favorites.length > 0 ? "fill-brand text-brand" : ""
                  }`}
              />
              {favorites.length > 0
                ? <span className="ml-1 text-xs">({favorites.length})</span>
                : null}
            </span>
          </Button>
        </div>
      </div>

      {/* Mobile header (lg:hidden): logo only; lg+ row shows logo + wordmark. */}
      <div className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)_auto] items-center gap-2 bg-white px-4 py-2 lg:hidden">
        <Link
          to="/"
          aria-label="BulkBuyPlace home"
          className="relative z-20 flex h-12 shrink-0 cursor-pointer items-center rounded-md px-2 no-underline text-inherit hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring outline-offset-2"
        >
          <img
            src="/images/cardboard_logo.svg"
            className="h-8 w-8 shrink-0 select-none"
            alt=""
            draggable={false}
          />
        </Link>

        <div className="relative z-0 flex min-h-0 min-w-0 max-w-full items-center justify-self-stretch self-stretch overflow-x-clip overflow-y-visible">
          <GlobalSearch category={props.category} />
        </div>

        <div className="flex shrink-0 items-center justify-end space-x-1 self-stretch">
          {/* Cart Icon */}
          {!props.hideCart && (
            <Button
              mode="ghost"
              size="sm"
              onClick={() => navigate("/cart")}
            >
              <ShoppingCart className="h-4 w-4" />
              {cartItems?.length > 0 && (
                <span className="ml-1 text-xs bg-red-500 text-white rounded-full px-1.5 py-0.5 min-w-[16px] text-center">
                  {cartItems.reduce((total, item) => total + item.quantity, 0)}
                </span>
              )}
            </Button>
          )}

          {/* Mobile Menu */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button mode="ghost" size="sm">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="mb-6">
                  <h2 className="text-lg font-semibold">Menu</h2>
                </div>

                {/* User Section */}
                {props.userId
                  ? (
                    <div className="mb-6">
                      <div className="flex items-center space-x-2 mb-4 p-3 bg-gray-50 rounded-lg">
                        <User className="h-5 w-5" />
                        <span className="font-medium">Hi {props.userName}</span>
                      </div>
                      <div className="space-y-2">
                        {props.isAdmin && (
                          <Button
                            mode="secondary"
                            className="w-full justify-start gap-2"
                            onClick={() => handleNavigation("/admin")}
                          >
                            <ArrowLeft className="h-4 w-4" />
                            Return to admin
                          </Button>
                        )}
                        <Button
                          mode="ghost"
                          className="w-full justify-start"
                          onClick={() => handleNavigation("/user")}
                        >
                          Account
                        </Button>
                        <Button
                          mode="ghost"
                          className="w-full justify-start"
                          onClick={() => handleNavigation("/user/orders")}
                        >
                          Orders
                        </Button>
                        <Button
                          mode="ghost"
                          className="w-full justify-start"
                          onClick={() => handleNavigation("/user/favorites")}
                        >
                          Favorites
                        </Button>
                      </div>
                    </div>
                  )
                  : (
                    <div className="mb-6">
                      <Button
                        mode="primary"
                        className="w-full"
                        onClick={() => handleNavigation("/login")}
                      >
                        Sign In
                      </Button>
                    </div>
                  )}

                {/* Cart Summary */}
                {cartItems && cartItems.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold mb-3">
                      Cart (
                      {cartItems.reduce(
                        (total, item) => total + item.quantity,
                        0,
                      )} items)
                    </h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {cartItems.slice(0, 3).map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center space-x-3 p-2 bg-gray-50 rounded"
                        >
                          <img
                            src={item.image || "/images/placeholder.svg"}
                            alt={item.name}
                            className="w-10 h-10 object-cover rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {item.name}
                            </div>
                            {item.store_name && (
                              <div className="text-xs text-blue-600 hover:text-blue-800">
                                <Link
                                  to={`/store/${slugify(item.store_name)}/home`}
                                >
                                  {item.store_name}
                                </Link>
                              </div>
                            )}
                          <div className="text-xs text-gray-500">
                            Qty: {item.quantity}
                          </div>
                        </div>
                        <CartLineTotalRight item={item} />
                        </div>
                      ))}
                      {cartItems.length > 3 && (
                        <div className="text-sm text-gray-500 text-center">
                          +{cartItems.length - 3} more items
                        </div>
                      )}
                    </div>
                    <Button
                      mode="primary"
                      className="w-full mt-3"
                      onClick={() => handleNavigation("/cart")}
                    >
                      View Full Cart
                    </Button>
                  </div>
                )}

                {/* Logout */}
                {props.userId && (
                  <div className="mt-auto">
                    <Button
                      mode="secondary"
                      className="w-full"
                      onClick={handleLogout}
                    >
                      Logout
                    </Button>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
